import React, {
  FunctionComponent as FC,
  useRef,
  useState,
  useEffect,
  useCallback
} from 'react';
import { useSelector, useDispatch } from 'react-redux'; // To call Action Creators
import { State } from '../../../state';
import { bindActionCreators } from "redux";
import { actionCreators } from "../../../state";
import './index.scss';

import { Spin } from 'antd';
import { select, zoom } from 'd3';
import {
  DownCircleOutlined,
} from '@ant-design/icons';
import _ from 'lodash';
import seedrandom from 'seedrandom';
import Panel from '../common/Panel';
import { viewSize } from '../../../types/style.global';
import { GlobalStyle } from '../../../types/style';
import { MainViewTypes, InteractionTypes, TooltipTypes } from '../../../types/data';


// import customized components
import ContextMenu from '../common/ContextMenu';
import Wordle from '../common/Visualization/Wordle';
import ImpactSummaryTable from './ImpactSummaryTable';
import SVGGroupWrapper from './SVGGroupWrapper';


// Seed the random number generator
// generate a stable force layout every time
// After seeding Math.random(), any usage of Math.random() in your D3 force simulation setup (or elsewhere in your code) will produce a consistent sequence of pseudo-random numbers, leading to a repeatable layout.
Math.random = seedrandom('3')


interface MainViewProps {
  viewParam: GlobalStyle.ViewSize;
  panelParam: GlobalStyle.PanelStyle;
  viewName: string;
  historicalRef?: React.RefObject<HTMLDivElement>;
}

const MainView: FC<MainViewProps> = (props) => {

  const { viewParam, panelParam, viewName, historicalRef: externalHistoricalRef } = props

  const dispatch = useDispatch()
  const {
    getMainViewData,
    getMainViewImpactSummaryTableData,
    updateMainViewLoading,
    onClickImpactFieldNode,
    onClickGrantFieldNode,

    updateMainViewGraphDivSize,
  } = bindActionCreators(actionCreators, dispatch)


  ////////////////////  (1) fetch data from data/ui reducer (end)  ////////////////////
  // data reducer
  const grantIDList: string[] = useSelector((state: State) => state.dataReducer?.grantIDList) as string[]
  const mainviewDataDict: MainViewTypes.MainViewDataDict = useSelector((state: State) => state.dataReducer?.mainviewDataDict) as MainViewTypes.MainViewDataDict
  // const isDrawGrantTypeNodeFinished: boolean = useSelector((state: State) => state.dataReducer?.isDrawGrantTypeNodeFinished) as boolean
  // const isEntityTypeGraphFinished: boolean = useSelector((state: State) => state.dataReducer?.isEntityTypeGraphFinished) as boolean
  const  impactSummaryData: MainViewTypes.ImpactSummaryViewData = useSelector((state: State) => state.dataReducer?.mainviewImpactSummaryTableData) as MainViewTypes.ImpactSummaryViewData

  // ui reducer
  const impactType: string = useSelector((state: State) => state.uiReducer?.impactType) as string
  const contextMenu: TooltipTypes.MainviewContextMenu = useSelector((state: State) => state.uiReducer?.mainviewContextMenu) as TooltipTypes.MainviewContextMenu
  const fieldLevelRange: InteractionTypes.UIParamFieldLevelRangeDict = useSelector((state: State) => state.uiReducer?.fieldLevelRange) as InteractionTypes.UIParamFieldLevelRangeDict
  const selectedImpactTypeList: string[] = useSelector((state: State) => state.uiReducer?.impactStatisticsTypeList) as string[]

  // loading related
  const mainViewLoadingLabel: string = useSelector((state: State) => state.dataReducer?.mainViewLoadingLabel) as string
  const isMainViewLoading: boolean = useSelector((state: State) => state.dataReducer?.isMainViewLoading) as boolean

  // post-process data
  let grantNodeGlyphData: MainViewTypes.GrantNodeGlyphDataUnit[] | undefined;
  let entityTypeGraphData: MainViewTypes.EntityTypeGraphData | undefined;
  let entityFieldGraphData: any | undefined;
  let predictionDataDict: MainViewTypes.PredictionResultDict | undefined;
  let wordleData: MainViewTypes.TotalWordleData | undefined;
  if (!_.isEmpty(mainviewDataDict)) {
    grantNodeGlyphData = mainviewDataDict.grantNodeGlyphData
    entityTypeGraphData = mainviewDataDict.entityTypeGraphData
    entityFieldGraphData = mainviewDataDict.entityFieldGraphData
    predictionDataDict = mainviewDataDict.predictionDataDict
    wordleData = mainviewDataDict.wordleData
  }
  // // debug
  // console.log('grantNodeGlyphData: ', grantNodeGlyphData)
  // console.log('entityTypeGraphData: ', entityTypeGraphData)
  // console.log('entityFieldGraphData: ', entityFieldGraphData)
  // console.log('predictionDataDict: ', predictionDataDict)
  // console.log('wordleData: ', wordleData)


  ////////////////////  (2) useEffect(): fetch mainview data from backend, using `grantIDList`  ////////////////////
  const updateMainView = async () => {
    try {
      // // opt 1 (abandoned)
      // // updateMainViewLoadingLabel('')
      // updateMainViewLoading(true)

      // opt 2: loading 状态现在由 getMainViewData 内部管理
      const result = await getMainViewData(fieldLevelRange, grantIDList)
    } catch (error) {
      console.error(error);
      // 只在出错时关闭 loading
      updateMainViewLoading(false)
    }
    // loading 会在数据获取完成后自动关闭
  }
  const updateMainViewImpactSummaryTable = async () => {
    try {
      const result = await getMainViewImpactSummaryTableData(grantIDList)
    } catch (error) {
      console.error(error);
    } finally {
      // console.log('updateMainViewImpactSummaryTable: finally')
    }
  }
  useEffect(() => {
    // console.log('in Main View useEffect')
    updateMainView()
    updateMainViewImpactSummaryTable()

    // // debug: spin css style
    // const timer = setTimeout(() => {
    //   // updateMainViewLoading(false)
    //   // updateMainViewLoadingLabel('')
    // }, 60000) // 60 seconds
    // return () => clearTimeout(timer)
  }, [fieldLevelRange, grantIDList
      // dispatch, grantIDList
     ])


  ////////////////////  (3) define global style (start)  ////////////////////
  const mainviewSVGRef = useRef(null as SVGSVGElement| null)
  const mainviewGroupRef = useRef(null as SVGGElement| null)
  const localHistoricalRef = useRef(null as HTMLDivElement| null)

  // Use external historicalRef if provided, otherwise use local
  const historicalRef = externalHistoricalRef || localHistoricalRef
  let svgBoundingBox;
  if (mainviewSVGRef.current) {
    svgBoundingBox = mainviewSVGRef.current.getBoundingClientRect()
  }
  // make the svg element zoomable and dragable
  const zoomBehaviorRef = useRef<any>(null);
  const [zoomInitialized, setZoomInitialized] = useState(false);

  // Initialize zoom behavior when SVG is available
  useEffect(() => {
    if (mainviewSVGRef.current) {
      const svg = select(mainviewSVGRef.current);

      // Create zoom behavior if it doesn't exist
      if (!zoomBehaviorRef.current) {
        zoomBehaviorRef.current = zoom()
          .scaleExtent([0.5, 10]) // Set the zoom range
          .filter((event) => {
            // Allow zoom on wheel events and drag events, but prevent conflicts with child interactions
            if (event.type === 'wheel') {
              return true;
            }
            if (event.type === 'mousedown') {
              // Allow drag only if clicking directly on SVG or the main group
              const target = event.target as Element;
              return target === mainviewSVGRef.current ||
                     target.id === 'mainview-g' ||
                     target.closest('#mainview-g') === null;
            }
            return false;
          })
          .on('zoom', (event) => {
            // Apply transform to group if it exists
            if (mainviewGroupRef.current && event && event.transform) {
              select(mainviewGroupRef.current).attr('transform', event.transform);
            }
          });
      }

      // Apply zoom behavior to SVG
      svg.call(zoomBehaviorRef.current);
      setZoomInitialized(true);

      // Cleanup function
      return () => {
        if (zoomBehaviorRef.current) {
          svg.on('.zoom', null); // Remove zoom event listeners
        }
      };
    }
  }, [mainviewSVGRef.current]);

  // Apply stored transform when group becomes available
  useEffect(() => {
    if (mainviewGroupRef.current && zoomInitialized && zoomBehaviorRef.current && mainviewSVGRef.current) {
      // Initialize group with identity transform if no transform exists
      const group = select(mainviewGroupRef.current);
      const currentTransform = group.attr('transform');
      if (!currentTransform) {
        group.attr('transform', 'translate(0,0) scale(1)');
      }
    }
  }, [mainviewGroupRef.current, zoomInitialized]);

  // Reset zoom function - can be called when zoom becomes unresponsive
  const resetZoom = useCallback(() => {
    if (mainviewSVGRef.current && mainviewGroupRef.current && zoomBehaviorRef.current) {
      const svg = select(mainviewSVGRef.current);
      const group = select(mainviewGroupRef.current);

      // Reset transform to identity
      const identity = { k: 1, x: 0, y: 0 };
      svg.call(zoomBehaviorRef.current.transform, identity);
      group.attr('transform', `translate(${identity.x},${identity.y}) scale(${identity.k})`);
    }
  }, []);

  // Add keyboard shortcut to reset zoom (optional)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Reset zoom with Ctrl + 0 or Cmd + 0
      if ((event.ctrlKey || event.metaKey) && event.key === '0') {
        event.preventDefault();
        resetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [resetZoom]);

  // Add click event to mainview SVG
  const handleClickMainviewSVG = useCallback(() => {
    // console.log('==> handleClickMainviewSVG()')
    // // clear up highlighted impact field node
    // onClickImpactFieldNode({
    //   impactType: impactType,
    //   impactEntity: '',
    //   impactFieldID: '',
    // })
    // // clear up highlighted grant field node
    // onClickGrantFieldNode({
    //   grantFieldID: '',
    //   grantFieldName: '',
    //   impactType: impactType,
    // })
  }, []);



  // responsive width and height based on user's browser window size
  const mainviewGraphDivRef = useRef<HTMLDivElement>(null);
  const [mainviewGraphDivSize, setMainviewGraphDivSize] = useState({ width: 0, height: 0 });
  // const [mainviewSVGWidth, setMainviewSVGWidth] = useState(0);
  // const [mainviewSVGHeight, setMainviewSVGHeight] = useState(0);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!mainviewGraphDivRef.current) return;

    const handleResize = (entries: ResizeObserverEntry[]) => {
      // Clear any existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Debounce the resize handling
      resizeTimeoutRef.current = setTimeout(() => {
        if (entries.length === 0) return;

        const entry = entries[0];
        const cr = entry.contentRect;
        const newWidth = cr.width;
        const newHeight = cr.height;

        // Only update if the size actually changed significantly (avoid micro-changes)
        const threshold = 1; // 1px threshold
        const currentSize = mainviewGraphDivSize;

        if (Math.abs(newWidth - currentSize.width) > threshold ||
            Math.abs(newHeight - currentSize.height) > threshold) {

          // Use requestAnimationFrame to batch DOM updates
          requestAnimationFrame(() => {
            // update state
            setMainviewGraphDivSize({ width: newWidth, height: newHeight });

            // update ui reducer
            updateMainViewGraphDivSize({ width: newWidth, height: newHeight })
          });
        }
      }, 16); // ~60fps throttling
    };

    // Handle ResizeObserver loop errors
    const ro = new ResizeObserver((entries) => {
      try {
        handleResize(entries);
      } catch (error) {
        // Suppress ResizeObserver loop errors - these are usually benign
        if (error instanceof Error && error.message.includes('ResizeObserver loop')) {
          console.warn('ResizeObserver loop detected, suppressing error');
          return;
        }
        console.error('ResizeObserver error:', error);
      }
    });

    ro.observe(mainviewGraphDivRef.current);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      ro.disconnect();
    };
  }, []); // Remove dependencies to prevent infinite loop

  // // debug
  // console.log('mainviewGraphDivSize: ', mainviewGraphDivSize)
  ////////////////////  (3) define global style (end)  ////////////////////



  ////////////////////  (4) draw element (start)  ////////////////////
  // Note: other elements are drawn in children components
  // 1. contextMenuElement
  const contextMenuElement = <ContextMenu />

  // 2. wordle
  let mainviewWordleData: MainViewTypes.TotalWordleData | undefined;
  if (!_.isEmpty(mainviewDataDict)) {
    mainviewWordleData = mainviewDataDict.wordleData
  }
  const wordleElement = mainviewWordleData ? <Wordle totalWordleData={mainviewWordleData} /> : null;
  ////////////////////  (4) draw element (end)  ////////////////////


  // console.log('mainViewLoadingLabel: ', mainViewLoadingLabel, isMainViewLoading)


  return (
    <Panel
      viewTitle={viewName}
      width={viewParam.width}
      height={viewParam.height}
      margin={panelParam.margin}
      padding={panelParam.padding}
      panelHeaderHeight={panelParam.panelHeaderHeight}
      historicalRef={historicalRef}
    >
      <div className='main-view-layout'>
        <Spin tip={mainViewLoadingLabel} spinning={isMainViewLoading} size='large'>
          <div className='summary-div' style={{
            width: viewSize.mainviewImpactSummary.width,
            margin: viewSize.mainviewImpactSummary.margin
          }}>
            { !_.isEmpty(impactSummaryData) && <ImpactSummaryTable data={impactSummaryData} /> }
          </div>
          <div className='graph-div' style={{
            width: viewSize.mainviewGraphDiv.width,
          }}
            ref={mainviewGraphDivRef}
          >
            <svg className='mainview-svg' ref={mainviewSVGRef}
                 style={{ width: '100%', height: '100%' }}
                 onClick={() => handleClickMainviewSVG()}
            >
              {/* { (selectedImpactTypeList.length > 0) && !_.isEmpty(mainviewDataDict) && !_.isEmpty(entityTypeGraphData) &&
                grantNodeGlyphData && (grantNodeGlyphData.length > 0) && !_.isEmpty(predictionDataDict) &&
                <SVGGroupWrapper mainviewGroupRef={mainviewGroupRef} svgBoundingBox={svgBoundingBox}
                  // data
                  entityTypeGraphData={entityTypeGraphData}
                  entityFieldGraphData={entityFieldGraphData}
                  grantNodeGlyphData={grantNodeGlyphData}
                  predictionDataDict={predictionDataDict} */}
              <g id='mainview-g' ref={mainviewGroupRef}>
                { (selectedImpactTypeList.length > 0) && !_.isEmpty(mainviewDataDict) && !_.isEmpty(entityTypeGraphData) && grantNodeGlyphData && (grantNodeGlyphData.length > 0) && !_.isEmpty(predictionDataDict) &&
                  <SVGGroupWrapper
                    // mainviewGroupRef={null} svgBoundingBox={svgBoundingBox}
                    // data
                    entityTypeGraphData={entityTypeGraphData}
                    entityFieldGraphData={entityFieldGraphData}
                    grantNodeGlyphData={grantNodeGlyphData}
                    predictionDataDict={predictionDataDict}

                    // ui
                    selectedImpactTypeList={selectedImpactTypeList}
                  />
                }
              </g>
              <defs>
                {/* for citation flow link */}
                <linearGradient id="fadeStroke" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="100%" y2="0">
                  <stop offset="0%" stopColor="#C3D4D5" stopOpacity={0} />
                  <stop offset="25%" stopColor="#C3D4D5" stopOpacity={1} />
                  <stop offset="65%" stopColor="#C3D4D5" stopOpacity={1} />
                  <stop offset="100%" stopColor="#C3D4D5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <defs>
                {/* for grant glyph highlight */}
                <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur in="SourceAlpha" stdDeviation="5"/>
                  <feOffset dx="3" dy="3" result="offsetblur"/>
                  <feFlood floodColor="#333333" floodOpacity="0.1"/>
                  <feComposite in2="offsetblur" operator="in"/>
                  <feMerge>
                    <feMergeNode/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>

                {/* for grant glyph css */}
                <radialGradient id="grant-glyph-prediction" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#98673D', stopOpacity: 1}} />
                  {/* <stop offset="13%" style={{stopColor: '#B08968', stopOpacity: 1}} /> */}
                  <stop offset="100%" style={{stopColor: '#EBE0D8', stopOpacity: 1}} />
                </radialGradient>
                {/* <filter id="filter-grant-glyph-prediction" x="-50%" y="-50%" width="200%" height="200%">
                  <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 1 0" />
                  <feOffset result="offOut" in="SourceAlpha" dx="0" dy="1" />
                  <feGaussianBlur result="blurOut" in="offOut" stdDeviation="5" />
                  <feBlend in="SourceGraphic" in2="blurOut" mode="normal" />
                </filter> */}
                <filter id="filter-grant-glyph-prediction" x="-50%" y="-50%" width="200%" height="200%">
                  <feOffset result="offOut" in="SourceAlpha" dx="0" dy="1" />
                  <feGaussianBlur result="blurOut" in="offOut" stdDeviation="6" />
                  <feComponentTransfer>
                    <feFuncA type="linear" slope="0.3" />
                  </feComponentTransfer>
                  <feMerge>
                    <feMergeNode />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <radialGradient id="RCI-glyph-directImpact-innerCircle" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#DDECED', stopOpacity: 1}} />
                  <stop offset="50%" style={{stopColor: '#DEECED', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#E8F1F2', stopOpacity: 1}} />
                </radialGradient>
                <radialGradient id="RCI-glyph-directImpact" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#006178', stopOpacity: 1}} />
                  <stop offset="50%" style={{stopColor: '#006178', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#A3C1C7', stopOpacity: 1}} />
                  {/* <stop offset="0%" style={{stopColor: '#006178', stopOpacity: 0.78}} />
                  <stop offset="27%" style={{stopColor: '#006178', stopOpacity: 0.78}} />
                  <stop offset="50%" style={{stopColor: '#A3C1C7', stopOpacity: 0.78}} /> */}
                </radialGradient>
                <radialGradient id="RCI-glyph-indirectImpact-innerCircle" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#DDECED', stopOpacity: 1}} />
                  <stop offset="50%" style={{stopColor: '#F4F9F9', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#F9FAFB', stopOpacity: 1}} />
                </radialGradient>
                <radialGradient id="RCI-glyph-indirectImpact" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#7EB9B7', stopOpacity: 1}} />
                  <stop offset="50%" style={{stopColor: '#7EB9B7', stopOpacity: 1}} />
                  <stop offset="100%" style={{stopColor: '#DCE2E9', stopOpacity: 1}} />
                </radialGradient>
                <radialGradient id="RCI-glyph-test" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: 'red', stopOpacity: 0.2}} />
                  <stop offset="100%" style={{stopColor: 'blue', stopOpacity: 1}} />
                </radialGradient>
              </defs>
              <defs>
                {/* <radialGradient id="leaf-node-fill-highlight" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#C3593C', stopOpacity: 1}} />
                  <stop offset="15%" style={{stopColor: '#C3593C', stopOpacity: 0.5}} />
                  <stop offset="100%" style={{stopColor: '#F2B09E', stopOpacity: 0.3}} />
                </radialGradient> */}
                <radialGradient id="leaf-node-fill-predict" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#AE7BAC', stopOpacity: 1}} />
                  <stop offset="20%" style={{stopColor: '#AE7BAC', stopOpacity: 0.9}} />
                  <stop offset="100%" style={{stopColor: '#EADCE9', stopOpacity: 0.5}} />
                  {/* background-image: radial-gradient(circle at 50% 50%, #AE7BAC 0%, #EADCE9 50%); */}
                </radialGradient>
                <radialGradient id="leaf-node-fill" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#006178', stopOpacity: 1}} />
                  <stop offset="6%" style={{stopColor: '#006178', stopOpacity: 0.5}} />
                  <stop offset="100%" style={{stopColor: '#A3C1C7', stopOpacity: 0.2}} />
                  {/* <stop offset="100%" style={{stopColor: '#A3C1C7', stopOpacity: 0.3}} /> */}
                </radialGradient>
                <radialGradient id="counter-node-fill-predict" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#AE7BAC', stopOpacity: 0.5}} />
                  <stop offset="6%" style={{stopColor: '#AE7BAC', stopOpacity: 0.4}} />
                  <stop offset="100%" style={{stopColor: '#EADCE9', stopOpacity: 0.3}} />
                </radialGradient>
                <radialGradient id="counter-node-fill" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#7EB9B7', stopOpacity: 0.5}} />
                  <stop offset="6%" style={{stopColor: '#7EB9B7', stopOpacity: 0.4}} />
                  <stop offset="100%" style={{stopColor: '#DCE2E9', stopOpacity: 0.3}} />
                </radialGradient>
                {/* <radialGradient id="counter-node-fill" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                  <stop offset="0%" style={{stopColor: '#7EB9B7', stopOpacity: 0.6}} />
                  <stop offset="6%" style={{stopColor: '#7EB9B7', stopOpacity: 0.5}} />
                  <stop offset="100%" style={{stopColor: '#DCE2E9', stopOpacity: 0.4}} />
                </radialGradient> */}
                <filter id="leaf-node-filter" x="0" y="0" width="100%" height="100%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
                  <feComponentTransfer in="blur" result="blur">
                    <feFuncA type="linear" slope="0.6" />
                  </feComponentTransfer>
                  <feComposite in="SourceGraphic" in2="blur" operator="in" result="composite" />
                </filter>
                {/* <pattern id={`stripe-#51473D`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
                  <path d="M 0 2.5 H 5" style={{strokeLinecap: 'square', strokeWidth: 3, stroke: '#51473D'}}></path>
                </pattern>
                <pattern id={`stripe-#E0E0E0`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
                  <path d="M 0 2.5 H 5" style={{strokeLinecap: 'square', strokeWidth: 3, stroke: '#E0E0E0'}}></path>
                </pattern>
                <pattern id={`stripe-${themeColor.defaultMode.chartFillColor}`} width="5" height="5" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
                  <path d="M 0 2.5 H 5" style={{strokeLinecap: 'square', strokeWidth: 3, stroke: themeColor.defaultMode.chartFillColor}}></path>
                </pattern> */}
              </defs>
            </svg>
            {
              (contextMenu.showContextMenu) && (
                                        <div style={{ position: 'absolute', top: contextMenu.y, left: contextMenu.x }}>
                                          <div onClick={() => console.log('Option 1 clicked')}>Option 1</div>
                                          <div onClick={() => console.log('Option 2 clicked')}>Option 2</div>
                                        </div>
                                     )
            }
          </div>
          <div className='interaction-notion-div'>
            <div className='interaction-notion-title'>Interaction Notion <DownCircleOutlined /></div>
            <div className='interaction-notion-items'>
              <div className='interaction-notion-item'>Scroll mouse to zoom in / out</div>
              <div className='interaction-notion-item'>Drag mouse to pan</div>
              <div className='interaction-notion-item'>Zoom in / out / reset zoom by using keyboard shortcut: Ctrl + 0 or Cmd + 0</div>
              <div className='interaction-notion-item'>Click on a grant node to highlight the grant node and its impact field nodes. Click again to <span style={{fontStyle: 'italic'}}>clear up</span> the highlight</div>
              <div className='interaction-notion-item'>Click on a impact field node to highlight the impact field node and its citation flow. Click again to <span style={{fontStyle: 'italic'}}>clear up</span> the highlight</div>
            </div>
          </div>
          <div className='wordle-div'>
            {wordleElement}
          </div>
          <div className='context-menu'>
            {contextMenuElement}
          </div>
        </Spin>
      </div>
    </Panel>
  )
}

export default MainView;